import React, { useEffect, useState } from 'react'
import { Box, Button, Flex, Heading, Input, Text, toast } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { TopupConfig } from 'api/highnote/topup'
import { useTopupConfig } from 'queries/highnote/useTopupConfig'
import { useUpdateTopupConfigMutation } from 'queries/highnote/useUpdateTopupConfigMutation'

type FieldKind = 'money' | 'days'
type EditableField =
  | 'minTopupThreshold'
  | 'forecastBufferAmount'
  | 'forecastLookaheadDays'

interface FieldMeta {
  key: EditableField
  label: string
  description: string
  kind: FieldKind
}

const FIELD_META: FieldMeta[] = [
  {
    key: 'minTopupThreshold',
    label: 'Min Topup Threshold',
    description:
      'Minimum available balance before a min-balance topup is triggered.',
    kind: 'money',
  },
  {
    key: 'forecastBufferAmount',
    label: 'Forecast Buffer Amount',
    description: 'Extra buffer added on top of the forecasted payout amount.',
    kind: 'money',
  },
  {
    key: 'forecastLookaheadDays',
    label: 'Forecast Lookahead Days',
    description: 'Number of days ahead the payout forecast looks.',
    kind: 'days',
  },
]

type FormState = Record<EditableField, string>

const toFormState = (config: TopupConfig): FormState => ({
  minTopupThreshold: Number(config.minTopupThreshold).toFixed(2),
  forecastBufferAmount: Number(config.forecastBufferAmount).toFixed(2),
  forecastLookaheadDays: String(config.forecastLookaheadDays),
})

const INITIAL_FORM: FormState = {
  minTopupThreshold: '',
  forecastBufferAmount: '',
  forecastLookaheadDays: '',
}

const formatCurrent = (config: TopupConfig, meta: FieldMeta) => {
  if (meta.kind === 'money') {
    return `$${Number(config[meta.key]).toFixed(2)}`
  }
  const days = config.forecastLookaheadDays
  return `${days} day${days === 1 ? '' : 's'}`
}

const formatSetBy = (config: TopupConfig) => {
  const who = config.actorName ?? `user #${config.actorId}`
  const when = new Date(config.configuredAt).toLocaleString('en-US')
  return `Set by ${who} on ${when}`
}

const isFieldValid = (value: string) => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed >= 0
}

export default function Topup() {
  const { data: config, isLoading, error } = useTopupConfig()
  const { mutateAsync: updateConfig, isPending } =
    useUpdateTopupConfigMutation()

  const [form, setForm] = useState<FormState>(() =>
    config ? toFormState(config) : INITIAL_FORM
  )

  // Re-sync the form whenever the persisted config values change (e.g. after
  // save). Depend on the primitive values, not the object identity, so an
  // unstable query reference can't trigger a render loop.
  useEffect(() => {
    if (config) {
      setForm(toFormState(config))
    }
  }, [
    config?.minTopupThreshold,
    config?.forecastBufferAmount,
    config?.forecastLookaheadDays,
  ])

  const isValid = FIELD_META.every((meta) => isFieldValid(form[meta.key]))

  const handleUpdate = async () => {
    try {
      await updateConfig({
        minTopupThreshold: form.minTopupThreshold.trim(),
        forecastBufferAmount: form.forecastBufferAmount.trim(),
        forecastLookaheadDays: parseInt(form.forecastLookaheadDays, 10),
      })
      toast.success('Updated Highnote topup config')
    } catch (e) {
      toast.error(parseErrorMessage(e) ?? 'Failed to update topup config')
    }
  }

  return (
    <Box p={4}>
      <Heading level={2} mb={3}>
        Highnote Topup Config
      </Heading>
      {isLoading ? <Text>Loading…</Text> : null}
      {error ? <Text color="error">Failed to load topup config</Text> : null}
      {!isLoading && !error && config ? (
        <Box
          p={3}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            maxWidth: 640,
          }}
        >
          {FIELD_META.map((meta) => (
            <Flex
              key={meta.key}
              justifyContent="space-between"
              alignItems="flex-start"
              gap={3}
              mb={3}
            >
              <Box>
                <Text fontWeight="bold">{meta.label}</Text>
                <Text fontSize="sm" color="lightText">
                  {meta.description}
                </Text>
                <Text fontSize="sm" mt={1}>
                  Current: {formatCurrent(config, meta)}
                </Text>
              </Box>
              <Flex alignItems="center" gap={2} style={{ flexShrink: 0 }}>
                <Box style={{ width: 12, textAlign: 'right' }}>
                  {meta.kind === 'money' ? <Text>$</Text> : null}
                </Box>
                <Input
                  type="number"
                  min={0}
                  step={meta.kind === 'money' ? '0.01' : '1'}
                  value={form[meta.key]}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, [meta.key]: e.target.value }))
                  }
                  style={{ width: 130 }}
                />
                <Box style={{ width: 32 }}>
                  {meta.kind === 'days' ? <Text>days</Text> : null}
                </Box>
              </Flex>
            </Flex>
          ))}
          <Flex justifyContent="space-between" alignItems="center" gap={3}>
            <Text fontSize="xs" color="lightText">
              {formatSetBy(config)}
            </Text>
            <Button
              onClick={handleUpdate}
              loading={isPending}
              disabled={!isValid}
            >
              Update
            </Button>
          </Flex>
        </Box>
      ) : null}
    </Box>
  )
}
